import {
  CALLABLE_NAMES,
  paths,
  type ActivateBusinessRequest,
  type AssistantVoice,
  type GreetingMode,
  type ActivateBusinessResponse,
  type BeginForwardingTestRequest,
  type BeginForwardingTestResponse,
  type CancelForwardingTestRequest,
  type CancelForwardingTestResponse,
  type CompleteOnboardingRequest,
  type CompleteOnboardingResponse,
  type DeclareSourceNumberRequest,
  type DeclareSourceNumberResponse,
  type Country,
  type CreateBusinessRequest,
  type CreateBusinessResponse,
  type ArchiveBusinessRequest,
  type ArchiveBusinessResponse,
  type Business,
  type BusinessType,
  type Language,
  type SetBusinessRegionRequest,
  type SetBusinessRegionResponse,
} from '@acs/shared';
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  bundleConverter,
  businessConverter,
  numberConverter,
  type BundleDocWithId,
  type BusinessDoc,
  type NumberDoc,
} from './converters.js';
import { db, functions } from './firebase.js';

/**
 * Fields an owner may edit — must stay within the Firestore rules' allow-list.
 *
 * `country` and `timezone` are deliberately absent: they move together and are
 * owned by the {@link setBusinessRegion} callable (rules reject them here).
 */
export interface BusinessSettingsPatch {
  name?: string;
  language?: Language;
  greeting?: string;
  /** Doc 71: `default` = our sentence from the current name; `custom` = own words. */
  greetingMode?: GreetingMode;
  /** The OpenAI Realtime voice callers hear. */
  voice?: AssistantVoice;
  slotGranularityMin?: number;
  /** Hard stop on call length, in seconds (schema bounds: 60–1800). */
  maxCallSeconds?: number;
  registration?: Partial<BusinessDoc['registration']>;
  /** What kind of business (doc 23). Open while the business is in setup (the
   * wizard's first step corrects it), fixed once setup is finished —
   * `firestore.rules` (`businessTypeEditable`) rejects any later change, and
   * Settings shows it read-only. */
  businessType?: BusinessType;
  /** Owner's mobile operator — drives the forwarding-fee warning (doc 13a). */
  ownerLineOperator?: string | null;
  /** Client-facing SMS (doc 21). Written as dotted field paths so toggling one
   * switch never clobbers a sibling a concurrent tab just changed. */
  sms?: Partial<Business['notifications']['sms']>;
  /** Owner-facing email (doc 29). Dotted like `sms`, and for the same reason —
   *  the two live in one `notifications` map. */
  email?: Partial<Business['notifications']['email']>;
  /** Reference notes the assistant answers from (doc 23 P1). Dotted like `sms`
   *  so the block is merged rather than replaced. */
  facts?: Partial<Business['facts']>;
  // `onboardingCompletedAt` is deliberately absent: `completeOnboarding` owns
  // it now, because finishing setup is conditional on the business being able
  // to answer a call (doc 18 §7.3) and firestore.rules no longer permits the
  // client to write it.
}

/** Live subscription to all businesses owned by the account, oldest first (doc 07 §8.2). */
export function subscribeMyBusinesses(uid: string, cb: (rows: BusinessDoc[]) => void): () => void {
  const q = query(
    collection(db, paths.businesses()).withConverter(businessConverter),
    where('ownerUid', '==', uid),
    orderBy('createdAt', 'asc'),
  );
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => d.data())));
}

export function subscribeBusiness(businessId: string, cb: (biz: BusinessDoc | null) => void): () => void {
  const ref = doc(db, paths.business(businessId)).withConverter(businessConverter);
  return onSnapshot(ref, (snap) => cb(snap.exists() ? snap.data() : null));
}

/** Live list of a business's purchased/in-flight numbers (doc 05 §3). */
export function subscribeNumbers(businessId: string, cb: (numbers: NumberDoc[]) => void): () => void {
  const q = query(
    collection(db, paths.numbers(businessId)).withConverter(numberConverter),
    orderBy('createdAt', 'asc'),
  );
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => d.data())));
}

/** Live list of a business's regulatory bundles (one per country+type). */
export function subscribeBundles(businessId: string, cb: (bundles: BundleDocWithId[]) => void): () => void {
  const q = collection(db, paths.bundles(businessId)).withConverter(bundleConverter);
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => d.data())));
}

const createBusinessFn = httpsCallable<CreateBusinessRequest, CreateBusinessResponse>(
  functions,
  CALLABLE_NAMES.createBusiness,
);
const activateBusinessFn = httpsCallable<ActivateBusinessRequest, ActivateBusinessResponse>(
  functions,
  CALLABLE_NAMES.activateBusiness,
);
const beginForwardingTestFn = httpsCallable<BeginForwardingTestRequest, BeginForwardingTestResponse>(
  functions,
  CALLABLE_NAMES.beginForwardingTest,
);
const cancelForwardingTestFn = httpsCallable<CancelForwardingTestRequest, CancelForwardingTestResponse>(
  functions,
  CALLABLE_NAMES.cancelForwardingTest,
);
const declareSourceNumberFn = httpsCallable<DeclareSourceNumberRequest, DeclareSourceNumberResponse>(
  functions,
  CALLABLE_NAMES.declareSourceNumber,
);
const completeOnboardingFn = httpsCallable<CompleteOnboardingRequest, CompleteOnboardingResponse>(
  functions,
  CALLABLE_NAMES.completeOnboarding,
);
const setBusinessRegionFn = httpsCallable<SetBusinessRegionRequest, SetBusinessRegionResponse>(
  functions,
  CALLABLE_NAMES.setBusinessRegion,
);
const archiveBusinessFn = httpsCallable<ArchiveBusinessRequest, ArchiveBusinessResponse>(
  functions,
  CALLABLE_NAMES.archiveBusiness,
);

/**
 * Create an additional business/location under the account (doc 07 §5.1). The
 * quota check, atomic business+availability+services write, and account-doc
 * bootstrap all happen server-side in the callable.
 */
export async function createBusiness(input: CreateBusinessRequest): Promise<string> {
  return (await createBusinessFn(input)).data.businessId;
}

/**
 * Take a draft business live (doc 13a): the server auto-buys a cheap
 * forward-target number and points it at the gateway. Returns the number the
 * owner should forward their real line to.
 */
export async function activateBusiness(businessId: string): Promise<ActivateBusinessResponse> {
  return (await activateBusinessFn({ businessId })).data;
}

/**
 * Open the forwarding-test window (doc 13a). The owner then places a call to
 * their own (forwarded) number; the gateway confirms it landed and marks the
 * business's forward number verified. Returns the number to trigger a call to.
 */
export async function beginForwardingTest(businessId: string): Promise<BeginForwardingTestResponse> {
  return (await beginForwardingTestFn({ businessId })).data;
}

/**
 * Close an open forwarding-test window (doc 18 D2) — what "Stop the check"
 * presses. The declared line is frozen while a check is running, so this is
 * also how an owner gets the field back to fix a typo.
 */
export async function cancelForwardingTest(
  businessId: string,
): Promise<CancelForwardingTestResponse> {
  return (await cancelForwardingTestFn({ businessId })).data;
}

/**
 * Tell the server which line the owner will divert to us (doc 18). Accepts any
 * shape the owner types and returns the canonical E.164 it stored, so the UI can
 * show them what was actually saved. Declaring does NOT connect anything — the
 * verification call is what grants routing.
 */
export async function declareSourceNumber(
  businessId: string,
  sourceNumber: string,
): Promise<DeclareSourceNumberResponse> {
  return (await declareSourceNumberFn({ businessId, sourceNumber })).data;
}

/**
 * Finish setup. Server-side (doc 18 §7.3) because it refuses while the business
 * cannot answer a call — a rule the portal cannot be the only enforcer of.
 */
export async function completeOnboarding(businessId: string): Promise<CompleteOnboardingResponse> {
  return (await completeOnboardingFn({ businessId })).data;
}

/**
 * Change the business's country (doc 13a). The server derives the scheduling
 * timezone from it and refuses once a national number is held — that number's
 * country is the business's country.
 */
export async function setBusinessRegion(
  businessId: string,
  country: Country,
): Promise<SetBusinessRegionResponse> {
  return (await setBusinessRegionFn({ businessId, country })).data;
}

/** Archive a business; its forward-target number is released automatically (doc 13a). */
export async function archiveBusiness(businessId: string): Promise<void> {
  await archiveBusinessFn({ businessId });
}

export async function updateBusinessSettings(
  businessId: string,
  patch: BusinessSettingsPatch,
): Promise<void> {
  const flat: Record<string, unknown> = { updatedAt: serverTimestamp() };
  for (const [k, v] of Object.entries(patch)) {
    if (k === 'registration' && v && typeof v === 'object') {
      for (const [rk, rv] of Object.entries(v)) flat[`registration.${rk}`] = rv;
    } else if (k === 'sms' && v && typeof v === 'object') {
      for (const [sk, sv] of Object.entries(v)) flat[`notifications.sms.${sk}`] = sv;
    } else if (k === 'email' && v && typeof v === 'object') {
      for (const [ek, ev] of Object.entries(v)) flat[`notifications.email.${ek}`] = ev;
    } else if (k === 'facts' && v && typeof v === 'object') {
      for (const [fk, fv] of Object.entries(v)) flat[`facts.${fk}`] = fv;
    } else {
      flat[k] = v;
    }
  }
  await updateDoc(doc(db, paths.business(businessId)), flat);
}
